import {
  filterModuleHasContent,
  mergeCustomOutputToAPIFiltersFormat,
  parseFilterModuleFiltersFromCubeFilters,
  ruleItemInjector,
  timePeriodToDayCount,
} from "../../../helper/Quadrant/quadrantHelper";
import {
  PlatformWiseKeyMapper,
  PlatformWiseQueryMeasures,
  PlatformWiseQueryOrder,
} from "../../../components/Filter/FilterData/quadrantFilter";

export { filterModuleHasContent };

const EXCLUDED_CAMPAIGN_ABO_MEASURES = [
  "VariantAnalysisTablePPMPlaceholder.BSR",
];


function formatDate(d) {
  return d.toISOString().slice(0, 10);
}

function dateRangeForPeriod(timePeriod) {
  const end = new Date();
  const start = new Date();
  const days = timePeriodToDayCount(timePeriod);
  start.setDate(end.getDate() - days);
  return [formatDate(start), formatDate(end)];
}

/** Build entity_metric_filter.cube_filters for Campaign ABO save payload. */
export function buildCubeFiltersFromFilterModule(
  filterModuleFilters,
  { timePeriod = "LAST_7_DAY", platformCampaignIds = [], userPlatform = "amazon" } = {}
) {
  if (!filterModuleHasContent(filterModuleFilters)) return "";

  const merged = mergeCustomOutputToAPIFiltersFormat(
    filterModuleFilters?.metrics?.custom_output,
    filterModuleFilters?.dimension?.custom_output,
    filterModuleFilters?.campaigns?.custom_output,
    filterModuleFilters?.topX?.custom_output
  );

  if (!Object.keys(merged).length) return "";

  let filters = merged;
  if (platformCampaignIds?.length) {
    filters = ruleItemInjector(filters, "dimensions", [
      {
        type: "RULE_ITEM",
        member: "campaign_id",
        operator: "in",
        values: platformCampaignIds.map(String),
      },
    ]);
  }

  const [dateStart, dateEnd] = dateRangeForPeriod(timePeriod);
  const resolvedPlatform = userPlatform || "amazon";
  const rawMeasures = resolvedPlatform === "amazon" ? PlatformWiseQueryMeasures["amazonCampaignABO"] : PlatformWiseQueryMeasures[resolvedPlatform];
  const filteredMeasures = rawMeasures.filter(
    (measure) => !EXCLUDED_CAMPAIGN_ABO_MEASURES.includes(measure)
  );
  const measures = filteredMeasures;
  const dimensions = ["target_id"];
  const order = resolvedPlatform === "amazon" ? PlatformWiseQueryOrder["amazonCampaignABO"] : PlatformWiseQueryOrder[resolvedPlatform] || PlatformWiseQueryOrder.amazon || [];
  const dateDimension = resolvedPlatform === "amazon" ? PlatformWiseKeyMapper["amazonCampaignABO"]?.date : PlatformWiseKeyMapper[resolvedPlatform]?.date;

  return JSON.stringify({
    query: {
      measures,
      dimensions,
      timeDimensions: [{ dimension: dateDimension, dateRange: [dateStart, dateEnd] }],
      filters,
      limit: 50,
      order,
    },
  });
}

/** Inclusive hour buckets for a dayparting slot, e.g. 02:00–04:00 → ["02","03","04"]. */
export function hourValuesInclusive(startTime, endTime) {
  const start = Number(String(startTime ?? "").slice(0, 2));
  const end = Number(String(endTime ?? "").slice(0, 2));
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
  const values = [];
  for (let hour = start; hour <= end; hour += 1) {
    values.push(String(hour).padStart(2, "0"));
  }
  return values;
}

function withoutHourOfDay(node) {
  if (!node || typeof node !== "object") return node;
  if (node.type === "RULE_ITEM" && node.member === "hour_of_day") return null;
  if (node.type === "AND_RULE" && Array.isArray(node.and)) {
    const and = node.and.map(withoutHourOfDay).filter(Boolean);
    return and.length ? { ...node, and } : null;
  }
  if (node.type === "OR_RULE" && Array.isArray(node.or)) {
    const or = node.or.map(withoutHourOfDay).filter(Boolean);
    return or.length ? { ...node, or } : null;
  }
  return node;
}

/**
 * Add hour_of_day the same way Product Report injects profileId:
 * ruleItemInjector wraps the rule in its own AND_RULE inside dimensions.and.
 */
export function injectHourOfDayDimension(cubeFiltersRaw, hourValues) {
  if (!hourValues?.length) return cubeFiltersRaw || "";

  let parsed = null;
  const raw = typeof cubeFiltersRaw === "string" ? cubeFiltersRaw.trim() : "";
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }
  if (!parsed || typeof parsed !== "object") {
    parsed = { query: { filters: {} } };
  }
  if (!parsed.query || typeof parsed.query !== "object") parsed.query = {};
  if (
    !parsed.query.filters ||
    typeof parsed.query.filters !== "object" ||
    Array.isArray(parsed.query.filters)
  ) {
    parsed.query.filters = {};
  }

  const filters = parsed.query.filters;
  const stripped = withoutHourOfDay(filters.dimensions);
  if (stripped) {
    filters.dimensions = stripped;
  } else {
    delete filters.dimensions;
  }

  const hourItem = {
    type: "RULE_ITEM",
    member: "hour_of_day",
    operator: "equals",
    values: hourValues.map(String),
  };
  parsed.query.filters = ruleItemInjector(filters, "dimensions", [hourItem]);

  return JSON.stringify(parsed);
}

/** Load edit form filters from entity_metric_filter.cube_filters. */
export function hydrateFilterModuleFromCubeFilters(cubeFiltersRaw) {
  return parseFilterModuleFiltersFromCubeFilters(cubeFiltersRaw, {
    stripMembers: ["campaign_id", "hour_of_day"],
  });
}

/** True when cube_filters still has user rules after hour/campaign injections are removed. */
export function cubeFiltersHaveUserRules(cubeFiltersRaw) {
  return filterModuleHasContent(hydrateFilterModuleFromCubeFilters(cubeFiltersRaw));
}
